import { useNavigate, useParams } from "react-router-dom"
import AppShell from "@/components/AppShell/AppShell"
import BreadcrumbBar from "@/components/BreadcrumbBar/BreadcrumbBar"
import Button from "@/components/Button/Button"
import StatusScreen from "@/components/StatusScreen/StatusScreen"
import Debrief from "./Debrief"
import { WRAP_COPY } from "@/content/wrap"
import { useSessionConnection } from "@/hooks/useSessionConnection"
import { ROUTES } from "@/routes/routes"
import { useSessionStore } from "@/store"
import {
  selectConnectionFailed,
  selectError,
  selectIsLoading,
  selectSession,
  selectSessionMissing,
} from "@/store/selectors"
import { sessionPath } from "@/utils/routePaths"
import { sessionState } from "@/utils/sessionLifecycle"

/**
 * A finished session's debrief, opened on its own.
 *
 * Subscribes to the whole session the same way the session screen does —
 * `connectSession` brings the jobs and the pains with it, and the sheet is
 * derived from those. No new query and no new service function: the report is
 * the same document read from a different address.
 *
 * There is deliberately no `GeneratingScreen` here. Opening an old report from
 * the index is navigation, and a manufactured pause in front of a document that
 * is already sitting there would be a lie about it.
 */
export default function ReportPage() {
  return (
    <AppShell>
      <ReportScreen />
    </AppShell>
  )
}

function ReportScreen() {
  const { sessionId } = useParams<{ sessionId: string }>()
  useSessionConnection(sessionId)

  const navigate = useNavigate()
  const session = useSessionStore(selectSession)
  const loading = useSessionStore(selectIsLoading)
  const missing = useSessionStore(selectSessionMissing)
  const failed = useSessionStore(selectConnectionFailed)
  const error = useSessionStore(selectError)

  if (!sessionId) {
    return (
      <StatusScreen nested eyebrow={WRAP_COPY.crumb} title="No session in the link.">
        <Button onClick={() => navigate(ROUTES.REPORTS)}>Back to reports</Button>
      </StatusScreen>
    )
  }

  // Terminal first: a dead listener keeps delivering the last snapshot it had.
  if (failed) {
    return (
      <StatusScreen
        nested
        eyebrow={WRAP_COPY.crumb}
        title="This screen stopped listening."
        detail="Reload to pick it back up — nothing has been lost."
        raw={error}
      >
        <Button onClick={() => window.location.reload()}>Reload</Button>
      </StatusScreen>
    )
  }

  if (missing) {
    return (
      <StatusScreen
        nested
        eyebrow={WRAP_COPY.crumb}
        title="There is no session here."
        detail="The link may be stale, or the session was deleted."
      >
        <Button onClick={() => navigate(ROUTES.REPORTS)}>Back to reports</Button>
      </StatusScreen>
    )
  }

  if (loading || !session) {
    return <StatusScreen nested eyebrow={WRAP_COPY.crumb} title="Loading the debrief." waiting />
  }

  /*
   * A report is a finished session's leave-behind, and this address promises
   * one. Rendering `Debrief` for a session still in its second round would hand
   * the facilitator a downloadable Markdown file of a workshop that has not
   * happened yet — and nothing in the file would say so. `sessionState` rather
   * than `status`, so a session that lapsed without ever being finished is not
   * quietly published as a report either. No tick is threaded through: both
   * sides of the deadline are refused here, so nothing about this gate changes
   * as one passes.
   */
  if (sessionState(session) !== "complete") {
    return (
      <StatusScreen
        nested
        eyebrow={WRAP_COPY.crumb}
        title="This session has no report yet."
        detail="A report is written when the session is finished. Open the session to carry on running it."
      >
        <Button onClick={() => navigate(sessionPath(session.id))}>Open the session</Button>
      </StatusScreen>
    )
  }

  return (
    <>
      <BreadcrumbBar
        trail={[
          { label: WRAP_COPY.crumb, to: ROUTES.REPORTS },
          { label: WRAP_COPY.reportTitle(session.customerName || session.name) },
        ]}
      />
      <Debrief standalone />
    </>
  )
}
